import WebCallsAPI from 'dashboard/api/webCalls';

// Same formats and cadence as the WhatsApp recorder (useWhatsappCallSession):
// the first type this browser can encode wins, and a 1s timeslice keeps the
// recording in memory as it goes. audio/mp4 is last — Safari is the only
// browser that needs it, and every other browser would otherwise pick it.
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];
const RECORDING_TIMESLICE_MS = 1000;
// A call is voice-only speech, not music: 32 kbps mono keeps hours of audio
// well under the upload size ceiling without an audible quality loss.
const RECORDER_AUDIO_BITS_PER_SECOND = 32_000;
// Checked in order against the recorded blob's MIME type to name the upload.
const RECORDING_EXTENSIONS = [
  ['ogg', 'ogg'],
  ['mp4', 'mp4'],
  ['webm', 'webm'],
];

// A tab carries one web call at a time, so the recording is module state that
// every caller shares, like the WhatsApp session.
let session = null;

const openSession = () => {
  const mimeType = RECORDER_MIME_CANDIDATES.find(type =>
    MediaRecorder.isTypeSupported(type)
  );
  if (!mimeType) return null;

  // No forced sampleRate: some devices (Firefox mics at 44.1 kHz) fail to
  // connect into a context pinned to a different rate. The device default
  // always matches what the mic actually produces.
  const audioContext = new AudioContext();
  // AudioContext starts suspended under most autoplay policies; without
  // resume() the mix is silence.
  audioContext.resume().catch(() => {});
  const destination = audioContext.createMediaStreamDestination();
  // Both parties mixed to one channel: a call recording has no stereo
  // content worth the extra bytes.
  destination.channelCount = 1;
  destination.channelCountMode = 'explicit';
  destination.channelInterpretation = 'speakers';
  const chunks = [];
  try {
    const mediaRecorder = new MediaRecorder(destination.stream, {
      mimeType,
      audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND,
    });
    mediaRecorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    mediaRecorder.start(RECORDING_TIMESLICE_MS);
    return {
      audioContext,
      destination,
      mediaRecorder,
      chunks,
      mimeType,
      tracks: new Set(),
    };
  } catch (_) {
    // A MediaRecorder that refuses to construct/start (isTypeSupported can
    // still lie) must not leak the context or leave a retry storm behind.
    audioContext.close().catch(() => {});
    return null;
  }
};

const connectTrack = track => {
  if (!track || session.tracks.has(track)) return;
  try {
    session.audioContext
      .createMediaStreamSource(new MediaStream([track]))
      .connect(session.destination);
    // Marked connected only once the connection actually succeeded, so one
    // party's failure never gets recorded as "handled".
    session.tracks.add(track);
  } catch (_) {
    // A single track's failure (e.g. a sample-rate mismatch) must not stop
    // the rest of the call from being recorded.
  }
};

// Called whenever any party's audio appears or is replaced (join, mute,
// hold). Recording begins with the first remote (customer/agent) audio —
// nothing before someone besides us is on the line — and a replaced or
// additional track joins the same recording. Remote tracks connect first so
// a failing local mic never costs the other parties' audio.
const start = ({ localTrack, remoteTracks = [] }) => {
  if (!session) {
    if (!remoteTracks.length) return;
    session = openSession();
    if (!session) return;
  }
  remoteTracks.forEach(connectTrack);
  connectTrack(localTrack);
};

const stopRecorder = mediaRecorder =>
  new Promise(resolve => {
    if (mediaRecorder.state === 'inactive') {
      resolve();
      return;
    }
    mediaRecorder.addEventListener('stop', resolve, { once: true });
    try {
      mediaRecorder.stop();
    } catch (_) {
      resolve();
    }
  });

// The session is taken before the first await, so a second end-of-call signal
// (hang-up racing peer-left) finds nothing and the call is uploaded once.
const stopAndUpload = async callId => {
  const current = session;
  session = null;
  if (!current) return;

  await stopRecorder(current.mediaRecorder);
  current.audioContext.close().catch(() => {});
  if (!current.chunks.length) return;

  const type = current.chunks[0].type || current.mimeType;
  const extension =
    RECORDING_EXTENSIONS.find(([needle]) => type.includes(needle))?.[1] ||
    'webm';
  try {
    await WebCallsAPI.uploadRecording(
      callId,
      new Blob(current.chunks, { type }),
      `call-recording.${extension}`
    );
  } catch (_) {
    // Best-effort, like the WhatsApp upload: the server keeps the first upload
    // of a call, and a lost one leaves the call without a recording.
  }
};

// Drops an in-progress recording instead of uploading it — for a join that
// never completed (e.g. a transfer whose complete() call failed after audio
// was already flowing). Uploading that partial clip would win the server's
// "first upload wins" idempotency and permanently shadow the real recording.
const discard = () => {
  const current = session;
  session = null;
  if (!current) return;
  if (current.mediaRecorder.state !== 'inactive') {
    try {
      current.mediaRecorder.stop();
    } catch (_) {
      /* noop */
    }
  }
  current.audioContext.close().catch(() => {});
};

// Hold keeps both legs connected but silences the customer's mic client-side,
// so the recording is paused for the duration instead of capturing dead air.
const pause = () => {
  if (!session || session.mediaRecorder.state !== 'recording') return;
  session.mediaRecorder.pause();
};

const resume = () => {
  if (!session || session.mediaRecorder.state !== 'paused') return;
  session.mediaRecorder.resume();
};

export function useWebCallRecorder() {
  return { start, stopAndUpload, discard, pause, resume };
}
